import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Clock, XCircle, Info, ArrowRight } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ErrorRecoveryModal = ({ 
  isOpen, 
  onClose, 
  errorDetails, 
  onRetry, 
  isRetrying 
}) => {
  const [countdown, setCountdown] = useState(0);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  useEffect(() => {
    if (isOpen && errorDetails?.retryDelay) {
      setCountdown(Math.ceil(errorDetails?.retryDelay / 1000));
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen, errorDetails?.retryDelay]);

  if (!isOpen) return null;

  const getErrorIcon = () => {
    switch (errorDetails?.type) {
      case 'RLS_POLICY':
        return <AlertTriangle className="w-16 h-16 text-yellow-500" />;
      case 'PROFILE_TIMEOUT':
        return <Clock className="w-16 h-16 text-blue-500" />;
      case 'DUPLICATE_ACCOUNT':
        return <Info className="w-16 h-16 text-purple-500" />;
      default:
        return <XCircle className="w-16 h-16 text-red-500" />;
    }
  };

  const getErrorColor = () => {
    switch (errorDetails?.type) {
      case 'RLS_POLICY':
        return 'yellow';
      case 'PROFILE_TIMEOUT':
        return 'blue';
      case 'DUPLICATE_ACCOUNT':
        return 'purple';
      default:
        return 'red';
    }
  };

  const color = getErrorColor();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`bg-gradient-to-r from-${color}-50 to-${color}-100 p-6 border-b border-${color}-200`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className={`p-3 bg-white rounded-full shadow-md`}>
                {getErrorIcon()}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{errorDetails?.title}</h2>
                <p className="text-sm text-gray-600 mt-1">Attempt #{errorDetails?.attempts || 1}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Main Message */}
          <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4`}>
            <p className="text-gray-800 text-lg leading-relaxed">{errorDetails?.message}</p>
          </div>

          {/* What Happened Section */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Info className="w-5 h-5 mr-2 text-blue-600" />
              What Happened?
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {errorDetails?.type === 'RLS_POLICY' && (
                <>
                  <p className="text-gray-700">• Your account was created successfully</p>
                  <p className="text-gray-700">• Organization workspace is being set up</p>
                  <p className="text-gray-700">• Database permissions are being configured</p>
                  <p className="text-gray-700">• This is a temporary timing issue</p>
                </>
              )}
              {errorDetails?.type === 'PROFILE_TIMEOUT' && (
                <>
                  <p className="text-gray-700">• Your account was created successfully</p>
                  <p className="text-gray-700">• Account setup is taking longer than expected</p>
                  <p className="text-gray-700">• This can happen during high traffic periods</p>
                  <p className="text-gray-700">• Check your email for a confirmation link</p>
                </>
              )}
              {errorDetails?.type === 'DUPLICATE_ACCOUNT' && (
                <>
                  <p className="text-gray-700">• An account with this email already exists</p>
                  <p className="text-gray-700">• You may have registered previously</p>
                  <p className="text-gray-700">• Try logging in with your existing credentials</p>
                </>
              )}
              {errorDetails?.type === 'SUBDOMAIN_TAKEN' && (
                <>
                  <p className="text-gray-700">• The subdomain you chose is already in use</p>
                  <p className="text-gray-700">• Each academy needs a unique subdomain</p>
                  <p className="text-gray-700">• Go back and choose a different subdomain</p>
                </>
              )}
              {errorDetails?.type === 'GENERAL_ERROR' && (
                <>
                  <p className="text-gray-700">• An unexpected error occurred</p>
                  <p className="text-gray-700">• This may be a temporary network or server issue</p>
                  <p className="text-gray-700">• Retrying usually resolves the problem</p>
                </>
              )}
            </div>
          </div>

          {/* Suggestions Section */}
          {errorDetails?.suggestions && errorDetails?.suggestions?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                What You Can Do
              </h3>
              <div className="bg-green-50 rounded-lg p-4 space-y-2">
                {errorDetails?.suggestions?.map((suggestion, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <span className="text-green-600 font-bold mt-0.5">{index + 1}.</span>
                    <p className="text-gray-700 flex-1">{suggestion}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Technical Details (Collapsible) */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
              className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              <span>Technical Details</span>
              <svg
                className={`w-5 h-5 transition-transform ${showTechnicalDetails ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showTechnicalDetails && (
              <div className="mt-3 bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-xs overflow-x-auto">
                <p className="text-red-400">Error Type: {errorDetails?.type}</p>
                <p className="text-yellow-400 mt-2">Details: {errorDetails?.technicalDetails}</p>
                <p className="text-blue-400 mt-2">Timestamp: {new Date()?.toISOString()}</p>
              </div>
            )}
          </div>

          {/* Retry Status */}
          {isRetrying && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-3">
              <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
              <div>
                <p className="text-blue-900 font-medium">Retrying registration...</p>
                <p className="text-blue-700 text-sm">Please wait while we attempt to complete your registration</p>
              </div>
            </div>
          )}

          {/* Countdown Timer */}
          {countdown > 0 && errorDetails?.canRetry && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center space-x-3">
              <Clock className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-yellow-900 font-medium">Waiting {countdown} seconds before retry...</p>
                <p className="text-yellow-700 text-sm">Giving the system time to complete setup</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
          {errorDetails?.canRetry && (
            <Button
              onClick={onRetry}
              disabled={isRetrying || countdown > 0}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-3 rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Retrying...</span>
                </>
              ) : countdown > 0 ? (
                <>
                  <Clock className="w-5 h-5" />
                  <span>Wait {countdown}s</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  <span>Retry Registration</span>
                </>
              )}
            </Button>
          )}

          {errorDetails?.fallbackAction && (
            <Button
              onClick={errorDetails?.fallbackAction?.action}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-2"
            >
              <ArrowRight className="w-5 h-5" />
              <span>{errorDetails?.fallbackAction?.label}</span>
            </Button>
          )}

          <Button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg transition-all"
          >
            Close
          </Button>
        </div>

        {/* Help Text */}
        <div className="px-6 py-4 bg-blue-50 border-t border-blue-100">
          <p className="text-sm text-blue-800">
            <strong>Need help?</strong> If this issue persists, please contact support with the technical details above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ErrorRecoveryModal;